import { MergeEventViewsResource, NudgeObject } from '@mark43/cad-api';
import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '~/api-client';
import { createStreamListener, useStream } from '../../../../socket/use-stream';
import { ManageEventMergeStore } from '../store/manage-merge-event-store';

type MergeEventData = {
    store: ManageEventMergeStore | undefined;
};

function useIntegratedManageEventMergeStore(agencyEventId: number): MergeEventData {
    const [store, setStore] = useState<ManageEventMergeStore | undefined>(undefined);
    const apiClient = useApiClient();

    const getInitialState = useMemo(() => {
        return () => {
            return apiClient
                .request<MergeEventViewsResource.GetForDepartmentAndParent>({
                    method: 'POST',
                    url: 'merge_event_views/department_and_parent/',
                    data: {
                        agencyEventId,
                    },
                })
                .then((resp) => {
                    const { availableAgencyEvents, parentAgencyEvent, childAgencyEvents } =
                        resp.data.data ?? {};
                    if (
                        availableAgencyEvents !== undefined &&
                        parentAgencyEvent !== undefined &&
                        childAgencyEvents !== undefined
                    ) {
                        const allItems = [
                            ...availableAgencyEvents,
                            ...childAgencyEvents,
                            parentAgencyEvent,
                        ];
                        const alreadyMergedEvents = [...childAgencyEvents, parentAgencyEvent];
                        const store = new ManageEventMergeStore(
                            allItems,
                            alreadyMergedEvents,
                            parentAgencyEvent
                        );
                        setStore(store);
                    }
                });
        };
    }, [apiClient, agencyEventId]);

    useEffect(() => {
        getInitialState();
    }, [getInitialState]);

    const fetchEventsUpdatesByIds = React.useMemo(() => {
        return (agencyEventIds: number[]) => {
            return apiClient
                .request<MergeEventViewsResource.GetActiveByAgencyEventIds>({
                    method: 'POST',
                    url: 'merge_event_views/ids/',
                    data: {
                        agencyEventIds,
                    },
                })
                .then(({ data }) => {
                    if (data.data?.availableAgencyEvents) {
                        store?.upsertItems(data.data.availableAgencyEvents);
                    }
                });
        };
    }, [apiClient, store]);

    const getEventIdsToUpdateFromEventNudges = React.useMemo(() => {
        return (eventNudges: NudgeObject[] = []): number[] => {
            return eventNudges.map((eventNudge) => eventNudge.id);
        };
    }, []);

    const getEventIdsInStoreFromNonEventNudges = React.useMemo(() => {
        return (nonEventNudges: NudgeObject[] = []): number[] => {
            return nonEventNudges
                .map((nonEventNudge) => {
                    return nonEventNudge.e;
                })
                .filter((eventId) => eventId !== undefined && store?.hasItem(eventId)) as number[];
        };
    }, [store]);

    const getPreviousEventIdsInStoreFromUnitStatusNudges = React.useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .map((unitStatusNudge) => {
                    return unitStatusNudge.pe;
                })
                .filter((eventId) => eventId !== undefined && store?.hasItem(eventId)) as number[];
        };
    }, [store]);

    const listener = useMemo(
        () =>
            createStreamListener({
                streamNames: ['nudge'],
                onMessage: (message) => {
                    // evict events that need to be evicted first so we don't pull updates for them
                    const eventIdsToEvict = getEventIdsInStoreFromNonEventNudges(
                        message.payload.map['EC'] ?? []
                    );

                    if (eventIdsToEvict.length > 0) {
                        store?.removeItems(eventIdsToEvict);
                    }

                    const eventIdsToUpdate = [
                        ...getEventIdsToUpdateFromEventNudges(message.payload.map['E']),
                        ...getEventIdsInStoreFromNonEventNudges(message.payload.map['HU']),
                        ...getEventIdsInStoreFromNonEventNudges(message.payload.map['USS']),
                        ...getEventIdsInStoreFromNonEventNudges(message.payload.map['UUL']),
                        ...getPreviousEventIdsInStoreFromUnitStatusNudges(
                            message.payload.map['USS']
                        ),
                        // this is n^2 list is tiny we dont care
                    ].filter((eventId) => !eventIdsToEvict.includes(eventId));
                    if (eventIdsToUpdate.length > 0) {
                        fetchEventsUpdatesByIds(eventIdsToUpdate);
                    }
                },
                onSocketStatusChange: (socketStatusChangeEvent) => {
                    if (
                        socketStatusChangeEvent.status === 'CONNECTED' &&
                        socketStatusChangeEvent.timesConnected > 1
                    ) {
                        getInitialState();
                    }
                },
            }),
        [
            store,
            getEventIdsInStoreFromNonEventNudges,
            getEventIdsToUpdateFromEventNudges,
            getPreviousEventIdsInStoreFromUnitStatusNudges,
            fetchEventsUpdatesByIds,
            getInitialState,
        ]
    );

    useStream(listener);

    return { store };
}

export function useManageEventMergeStore(parentAgencyEventId: number): MergeEventData {
    const { store } = useIntegratedManageEventMergeStore(parentAgencyEventId);

    return { store };
}
